import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ShieldX } from 'lucide-react';
import type { StatoOta } from '@emacomfort/domain';
import { useDevice, useStato } from '@/app/DeviceProvider';
import { Avviso, Badge, Bottone, Card, Conferma, Pagina, stili } from '@/ui';
import { useSondaggio, useTecnico } from '../comune';
import s from '../features.module.css';

export function FirmwarePage() {
  const { t } = useTranslation();
  const { esegui } = useTecnico();
  const { store, client } = useDevice();
  const impianto = useStato((st) => st.impianto);
  const [conferma, setConferma] = useState(false);
  const carica = useCallback(() => esegui((tec, sess) => tec.getOta(sess)), [esegui]);
  const { dati: ota, ricarica } = useSondaggio<StatoOta>(carica, 600);

  if (!impianto || !ota) return null;
  const inCorso = ota.fase === 'pubblicazione' || ota.fase === 'rollout' || ota.fase === 'conferma' || ota.fase === 'rollback';
  const tono = ota.fase === 'completato' ? 'ok' : ota.fase === 'rifiutato' ? 'err' : inCorso ? 'info' : 'neutro';

  const dopo = async () => {
    await ricarica();
    store.setState({ impianto: await client.getImpianto() });
  };

  return (
    <Pagina titolo={t('tecnico.firmware.titolo')}>
      <Card>
        <div className={s.kpi}>
          <div className={s.kpiVoce}>
            <span className={stili.etichetta}>{t('tecnico.firmware.corrente')}</span>
            <span className={stili.valoreSecondario}>{ota.versioneCorrente}</span>
          </div>
          <div className={s.kpiVoce}>
            <span className={stili.etichetta}>{t('tecnico.firmware.disponibile')}</span>
            <span className={stili.valoreSecondario}>{ota.versioneDisponibile ?? '—'}</span>
          </div>
          <div className={s.kpiVoce}>
            <span className={stili.etichetta}>{t('tecnico.firmware.fase.inattivo')}</span>
            <Badge tono={tono}>{t(`tecnico.firmware.fase.${ota.fase}`)}</Badge>
          </div>
        </div>
        {impianto.centralina.tipo === 'terze_parti' && <Avviso tono="info">{t('tecnico.firmware.terzeParti')}</Avviso>}
        {inCorso && (
          <div className={s.progresso} role="progressbar" aria-valuenow={ota.avanzamentoPct} aria-valuemin={0} aria-valuemax={100}>
            <div className={s.progressoBarra} style={{ width: `${ota.avanzamentoPct}%` }} />
          </div>
        )}
        <div className={stili.riga}>
          <Badge tono={ota.firmata ? 'ok' : 'err'} icona={ota.firmata ? <ShieldCheck size={16} aria-hidden /> : <ShieldX size={16} aria-hidden />}>
            {ota.firmata ? t('tecnico.firmware.firmata') : t('tecnico.firmware.nonFirmata')}
          </Badge>
        </div>
        {ota.fase === 'rifiutato' && <Avviso tono="err">{t('tecnico.firmware.rifiutatoNota')}</Avviso>}
        {impianto.centralina.tipo === 'emacomfort' && (
          <div className={stili.riga}>
            {ota.versioneDisponibile ? (
              <Bottone variante="primario" disabled={inCorso} onClick={() => setConferma(true)}>
                {t('tecnico.firmware.avvia', { v: ota.versioneDisponibile })}
              </Bottone>
            ) : (
              <span className={stili.muto}>{t('tecnico.firmware.nessuno')}</span>
            )}
            {ota.versionePrecedente && (
              <Bottone variante="pericolo" disabled={inCorso} onClick={() => void esegui((tec, sess) => tec.rollbackOta(sess)).then(dopo)}>
                {t('tecnico.firmware.rollback', { v: ota.versionePrecedente })}
              </Bottone>
            )}
            <span className={stili.spazio} />
            <Bottone variante="fantasma" disabled={inCorso} onClick={() => void esegui((tec, sess) => tec.avviaOta(sess, { firmata: false })).then(dopo)}>
              <ShieldX size={22} aria-hidden />
              {t('tecnico.firmware.testNonFirmato')}
            </Bottone>
          </div>
        )}
        <p className={s.letturaNota}>{t('tecnico.firmware.testNonFirmatoNota')}</p>
      </Card>
      <Conferma
        aperta={conferma}
        titolo={t('tecnico.firmware.confermaAvvio', { v: ota.versioneDisponibile ?? '' })}
        onAnnulla={() => setConferma(false)}
        onConferma={() => {
          setConferma(false);
          void esegui((tec, sess) => tec.avviaOta(sess, { firmata: true })).then(dopo);
        }}
      />
    </Pagina>
  );
}
